/**
 * Quick Start / Integration guide page.
 */

import React, { useState } from 'react';
import {
  Terminal,
  Code2,
  Zap,
  Shield,
  Copy,
  CheckCircle2,
  BookOpen,
  ExternalLink,
} from 'lucide-react';

interface Snippet {
  id: string;
  title: string;
  description: string;
  language: string;
  code: string;
}

const SNIPPETS: Snippet[] = [
  {
    id: 'install',
    title: '1. Install the SDK',
    description: 'Add token-kit to your project via npm or yarn.',
    language: 'bash',
    code: `npm install @codetunezstudios/token-kit
# or
yarn add @codetunezstudios/token-kit`,
  },
  {
    id: 'init',
    title: '2. Initialize the Client',
    description: 'Create a TokenKit instance with your API key. Keep your key server-side.',
    language: 'typescript',
    code: `import TokenKit from '@codetunezstudios/token-kit';

const tk = new TokenKit({
  apiKey: process.env.TOKENKIT_API_KEY!,
  // Optional: override base URL for self-hosted
  // baseUrl: 'https://api.token-kit.com',
});`,
  },
  {
    id: 'balance',
    title: '3. Check User Balance',
    description: 'Verify a user has enough tokens before making an LLM call.',
    language: 'typescript',
    code: `const balance = await tk.getBalance(userToken);

if (balance < estimatedCost) {
  // Redirect user to purchase tokens at ai-tokens.me
  return res.json({
    needsTokens: true,
    purchaseUrl: 'https://ai-tokens.me/register',
  });
}`,
  },
  {
    id: 'chat',
    title: '4. Make an LLM Request',
    description: 'Send a chat completion request. Tokens are deducted from the user\'s balance automatically after a successful response.',
    language: 'typescript',
    code: `const response = await tk.chat(
  'ut_user123_abc456',
  [
    { role: 'system', content: 'You are a helpful assistant.' },
    { role: 'user', content: userMessage },
  ],
  {
    model: 'gpt-4o',            // or 'claude-sonnet-4', 'nova-micro'
    maxTokens: 1024,
    temperature: 0.7,
  }
);

console.log(response.message.content);
console.log('Tokens used:', response.tokensDeducted);`,
  },
  {
    id: 'streaming',
    title: '5. Multi-Turn Conversation',
    description: 'Build a conversational experience by managing message history.',
    language: 'typescript',
    code: `import TokenKit, { Message } from '@codetunezstudios/token-kit';

const history: Message[] = [
  TokenKit.system('You are a helpful coding tutor.')
];

// Turn 1
history.push(TokenKit.user('What is TypeScript?'));
const reply = await tk.chat(userToken, history);
history.push(reply.message);
console.log(reply.message.content);

// Turn 2 (has full context)
history.push(TokenKit.user('How is it different from JS?'));
const reply2 = await tk.chat(userToken, history);
history.push(reply2.message);`,
  },
  {
    id: 'express',
    title: '6. Express.js Integration',
    description: 'Full route handler example with error handling.',
    language: 'typescript',
    code: `import express from 'express';
  import TokenKit, { TokenKitAPIError } from '@codetunezstudios/token-kit';

const app = express();
const tk = new TokenKit({ apiKey: process.env.TOKENKIT_API_KEY! });

app.post('/api/chat', async (req, res) => {
  try {
    const { userToken, message } = req.body;

    const response = await tk.chat(
      userToken,
      [{ role: 'user', content: message }],
      { model: 'gpt-4o' }
    );

    res.json({
      content: response.message.content,
      tokensUsed: response.tokensDeducted,
    });
  } catch (err) {
    if (err instanceof TokenKitAPIError) {
      if (err.code === 'INSUFFICIENT_BALANCE') {
        return res.status(402).json({
          error: 'Insufficient tokens',
          purchaseUrl: 'https://ai-tokens.me/register',
        });
      }
    }
    res.status(500).json({ error: 'Internal server error' });
  }
});`,
  },
  {
    id: 'webhooks',
    title: '7. Handling Webhooks (Coming Soon)',
    description: 'Receive real-time events for token purchases, balance warnings, and more. This feature is planned for a future release.',
    language: 'typescript',
    code: `app.post('/webhooks/tokenkit', express.json(), (req, res) => {
  const event = req.body;

  switch (event.type) {
    case 'token.purchased':
      console.log(\`User \${event.data.userId} purchased \${event.data.amount} tokens\`);
      break;
    case 'token.depleted':
      console.log(\`User \${event.data.userId} has run out of tokens\`);
      // Send in-app notification
      break;
    case 'usage.limit_reached':
      console.log(\`User \${event.data.userId} hit their spending limit\`);
      break;
  }

  res.status(200).json({ received: true });
});`,
  },
];

export default function QuickStartPage() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (id: string, code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="quickstart-page">
      <div className="page-header">
        <div>
          <h1>Quick Start</h1>
          <p>Integrate token-kit into your application in minutes</p>
        </div>
        <a href="https://docs.token-kit.com" target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
          <BookOpen size={16} /> Full Docs <ExternalLink size={14} />
        </a>
      </div>

      {/* ── Overview cards ────────────────────────────────── */}
      <div className="qs-overview">
        <div className="qs-card">
          <Terminal size={24} />
          <h3>TypeScript SDK</h3>
          <p>Full type safety, auto-complete, and error types.</p>
        </div>
        <div className="qs-card">
          <Zap size={24} />
          <h3>One API Call</h3>
          <p>Balance check → LLM request → token deduction in one call.</p>
        </div>
        <div className="qs-card">
          <Shield size={24} />
          <h3>Secure by Default</h3>
          <p>API keys stay server-side. User tokens are scoped and rotatable.</p>
        </div>
      </div>

      {/* ── Code snippets ─────────────────────────────────── */}
      <div className="snippet-list">
        {SNIPPETS.map((s) => (
          <div key={s.id} className="snippet-block">
            <div className="snippet-header">
              <div>
                <h2>{s.title}</h2>
                <p>{s.description}</p>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => handleCopy(s.id, s.code)}>
                {copiedId === s.id ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
            <div className="code-block">
              <pre><code className={`language-${s.language}`}>{s.code}</code></pre>
            </div>
          </div>
        ))}
      </div>

      {/* ── Next steps ────────────────────────────────────── */}
      <div className="next-steps">
        <h2>What's Next?</h2>
        <ul>
          <li><Code2 size={16} /> Explore the <strong>SDK reference</strong> for advanced patterns like multi-turn conversations.</li>
          <li><Zap size={16} /> Check the <strong>developer dashboard</strong> to monitor usage and performance.</li>
          <li><Shield size={16} /> Configure <strong>rate limiting</strong> and API key rotation for production security.</li>
        </ul>
      </div>
    </div>
  );
}
